<script setup lang="ts">
    import { ref, toRefs, computed } from "vue";
    import emitter from "tiny-emitter/instance";

    import ModalBasic from "@/components/generic/ModalBasic.vue";
    import AdminLockBorder from "@/components/AdminLockBorder.vue";
    import { openDialog } from "@/utils";
    import store from "@/store";
    import KForm from "@/components/forms/Form/KForm.vue";
    import { AxiosError } from "axios";
    import { onBeforeMount, onBeforeUnmount } from "vue";
    import { flashWarning } from "@/utils";
    import { NumberValidationRule } from "@/components/forms/inputs/Number/numberValidation";
    import { TypAvGenomlysningsapparat } from "@/store/modules/typAvGenomlysningsapparatModule";

    const props = defineProps<{
        tabId: number; // TODO Ändra i Vue3
        parameters?: {
            typAvGenomlysningsapparatId: string;
        };
    }>();

    const { parameters, tabId } = toRefs(props);

    const errors = ref<{
        [key: string]: string[];
    }>({});
    const dialog = ref();
    const formRef = ref();

    const typAvGenomlysningsapparat = computed(() =>
        store.getters["typAvGenomlysningsapparat/typAvGenomlysningsapparatById"]({
            id: parameters?.value?.typAvGenomlysningsapparatId,
        })
    );
    const isEditing = computed(() => !!typAvGenomlysningsapparat.value);

    defineExpose({
        tabId,
    });

    const cancelForm = async () => {
        if (!formRef.value.hasUnsavedData()) {
            store.dispatch("tabs/closeTab", tabId.value);

            return;
        }

        emitter.emit("open-dialog", {
            type: "warning",
            description: "Du har osparad data. Vill du stänga utan att spara?",
            buttons: [
                {
                    title: "Fortsätt redigera",
                    type: "secondary",
                },
                {
                    title: "Ja, stäng utan att spara",
                    type: "primary",
                    action: () => {
                        store.dispatch("tabs/closeTab", tabId.value);
                    },
                },
            ],
        });
    };

    const setTabName = () => {
        store.commit("tabs/setTabName", {
            id: tabId.value,
            name: isEditing.value ? "Redigera typ av genomlysningsapparat" : "Skapa typ av genomlysningsapparat",
        });
    };

    // TODO convert to useTabs in v3
    const closeTab = ({
        override,
    }: {
        override?: boolean;
    } = {}) => {
        if (!override && formRef.value.hasUnsavedData()) {
            emitter.emit("open-dialog", {
                type: "warning",
                description: "Du har osparad data. Vill du stänga utan att spara?",
                buttons: [
                    {
                        title: "Fortsätt redigera",
                        type: "secondary",
                    },
                    {
                        title: "Ja, stäng utan att spara",
                        type: "primary",
                        action: () => {
                            store.dispatch("tabs/closeTab", tabId.value);
                        },
                    },
                ],
            });

            return;
        }

        store.dispatch("tabs/closeTab", tabId.value);
    };

    const onSubmit = async (typAvGenomlysningsapparat: unknown) => {
        try {
            if (isEditing.value) {
                await store.dispatch("typAvGenomlysningsapparat/updateTypAvGenomlysningsapparat", {
                    typAvGenomlysningsapparat: typAvGenomlysningsapparat as TypAvGenomlysningsapparat,
                });
            } else {
                await store.dispatch("typAvGenomlysningsapparat/createTypAvGenomlysningsapparat", {
                    typAvGenomlysningsapparat: typAvGenomlysningsapparat as TypAvGenomlysningsapparat,
                });
            }

            closeTab({ override: true });
        } catch (thrownError) {
            const error = thrownError as AxiosError;

            if (error?.response?.data) {
                errors.value = error.response.data;

                if (error?.response?.data?.non_field_errors && error?.response?.data?.non_field_errors.length > 0) {
                    openDialog(error?.response?.data?.non_field_errors[0], "error");
                }

                return;
            }

            flashWarning(
                isEditing.value
                    ? "Kunde inte redigera typ av genomlysningsapparat"
                    : "Kunde inte lägga till ny typ av genomlysningsapparat"
            );
        }
    };

    const fetchFormData = async () => {
        return (
            typAvGenomlysningsapparat.value ?? {
                namn: null,
                kod: null,
                ordning: null,
            }
        );
    };

    onBeforeMount(async () => {
        await store.dispatch("typAvGenomlysningsapparat/addSubscriber");

        setTabName();
    });

    onBeforeUnmount(async () => {
        await store.dispatch("typAvGenomlysningsapparat/removeSubscriber");
    });
</script>

<template>
    <div class="typ-av-genomlysningsapparat">
        <modal-basic ref="dialog" />
        <KForm
            ref="formRef"
            id="typAvGenomlysningsapparat"
            :use-formulate="true"
            :submit="onSubmit"
            submit-content="Spara och stäng"
            cancel-content="Avbryt"
            :errors="errors"
            :initial-values-getter="fetchFormData"
            @cancel="cancelForm"
            class="typ-av-genomlysningsapparat-form"
        >
            <template #header>
                <admin-lock-border />
                <h2 v-if="isEditing">Redigera typ av genomlysningsapparat</h2>
                <h2 v-else>Lägg till typ av genomlysningsapparat</h2>
            </template>
            <template #default>
                <div class="form-content">
                    <KText name="namn" label="Namn" validation="required" />

                    <KText name="kod" label="Kod" />

                    <KNumber name="ordning" label="Ordning" :validation="[[NumberValidationRule.Min, 0]]" />
                </div>
            </template>
        </KForm>
    </div>
</template>

<style lang="scss"></style>
